// Shared algorithm/trigger resolver used by every surface that shows
// which upstream algo fired a signal (HistoryTable on the home page,
// Toast notifications, GBEMI + PADÀ Monitor, GBEMI Monitor, STRATA Monitor,
// P1-S1 Monitor). Single source of truth keeps the rules consistent.
//
// Three layers of logic, in increasing complexity:
//   1. algoFromSignalId        — parse the algo prefix from a signal_id
//   2. buildTriggerIndex       — given an array of decisions, build the
//                                 same-bar-primaries + entry-bar maps used
//                                 to resolve LUX confluence + EXIT inheritance
//   3. resolveTrigger          — return the trigger label for a single
//                                 decision using a prebuilt index (or null
//                                 if no index is available — falls back to
//                                 plain signal_id parsing)

// ── 1. signal_id prefix parser ───────────────────────────────────────────
//
// Examples:
//   MW15_long_20260601_0933                       -> "MW15"
//   Trident_long_20260601_0933                    -> "Trident"
//   Cobra_long_20260601_1021                      -> "Cobra"
//   Cobra_long_20260601_1021_EXIT                 -> "Cobra"
//   LUX_long_20260601_0930                        -> "LUX"
//   AA-Swing_strong_bullish_long_20260601_0736    -> "AA-Swing_strong_bullish"
// Returns null if the id is missing or doesn't carry the expected shape.
export function algoFromSignalId(signalId) {
  if (!signalId) return null;
  const m = String(signalId).match(/^(.+?)_(long|short)_/i);
  if (m) return m[1];
  const first = String(signalId).split('_')[0];
  return first || null;
}

// ── 2. Build the index used by resolveTrigger() ──────────────────────────
//
// Returns: { sameBarPrimaries: { [bar_time]: Set<algo> },
//            entryBarByBaseSid: { [base_signal_id]: bar_time } }
//
// "Primary" = any non-LUX algo. EXIT rows are excluded from both maps because
// we want to resolve EXITs by inheritance from their original entry, not by
// the EXIT bar's neighbours.
export function buildTriggerIndex(decisions) {
  const sameBarPrimaries = {};
  const entryBarByBaseSid = {};
  for (const d of decisions || []) {
    const sid = d.signal_id;
    if (!sid) continue;
    const isExit = sid.endsWith('_EXIT');
    const baseSid = isExit ? sid.slice(0, -5) : sid;
    if (!isExit) entryBarByBaseSid[baseSid] = d.bar_time || d.signal_ts;
    if (isExit) continue;
    const algo = algoFromSignalId(sid);
    if (!algo || algo === 'LUX') continue;
    const barKey = d.bar_time || d.signal_ts;
    if (!barKey) continue;
    if (!sameBarPrimaries[barKey]) sameBarPrimaries[barKey] = new Set();
    sameBarPrimaries[barKey].add(algo);
  }
  return { sameBarPrimaries, entryBarByBaseSid };
}

// ── 3. resolveTrigger ────────────────────────────────────────────────────
//
// Returns { trigger, isLuxConfluencing }.
//
//  - trigger              — string label to render in the Algos column /
//                            on toasts. For LUX rows with same-bar primaries
//                            this is the primary (or "Cobra+Trident" joined).
//                            For standalone LUX it's "LUX". For non-LUX
//                            rows it's the parsed algo from signal_id.
//                            null if nothing identifiable.
//  - isLuxConfluencing    — true iff this row's signal_id starts with LUX
//                            AND a same-bar primary was found. UI typically
//                            appends a small "+LUX" suffix when this is set
//                            (caller's choice).
//
// `index` may be null/omitted — in which case the resolver falls back to a
// plain prefix parse (no LUX confluence, no EXIT inheritance). That's the
// right behaviour when you only have a single decision in hand (e.g. inside
// a Toast that doesn't carry the full feed).
export function resolveTrigger(decision, index) {
  if (!decision) return { trigger: null, isLuxConfluencing: false };
  // Explicit emitter trigger (e.g. JUYI carries COBRA / MW15 / AA_SCALP). When a
  // feed names its actual trigger algo we trust it over the signal_id prefix parse
  // (whose prefix is the system name, not the algo). Only LUX systems rely on the
  // signal_id confluence path below, and none of them populate `trigger`.
  if (decision.trigger && typeof decision.trigger === 'string' && decision.trigger.trim()) {
    return { trigger: decision.trigger.trim(), isLuxConfluencing: false };
  }
  const sid = decision.signal_id;
  if (!sid) {
    return { trigger: decision.source || null, isLuxConfluencing: false };
  }
  const isExit = sid.endsWith('_EXIT');
  const baseSid = isExit ? sid.slice(0, -5) : sid;
  const raw = algoFromSignalId(baseSid);
  if (!raw) return { trigger: decision.source || null, isLuxConfluencing: false };

  if (raw !== 'LUX' || !index) {
    return { trigger: raw, isLuxConfluencing: false };
  }

  const lookupBar = isExit
    ? index.entryBarByBaseSid[baseSid]
    : (decision.bar_time || decision.signal_ts);
  const primaries = lookupBar && index.sameBarPrimaries[lookupBar];
  if (primaries && primaries.size > 0) {
    return {
      trigger: Array.from(primaries).sort().join('+'),
      isLuxConfluencing: true,
    };
  }
  return { trigger: 'LUX', isLuxConfluencing: false };
}
